import { useState } from 'react';

import {
  JIANYING_TEMPLATE_RUNTIME_STATUS,
} from '@/features/systemManagement/jianying/constants/jianyingTemplateStatus.js';
import {
  createJianyingJob,
} from '../../../services/jianyingJobService.js';
import {
  uploadJianyingJobAudio,
} from '../../../services/jianyingAudioUploadService.js';
import {
  uploadJianyingJobVideo,
} from '../../../services/jianyingVideoUploadService.js';
import { validateJianyingSlotValues } from '../../../utils/jianyingSlots.js';
import {
  getJianyingCreateErrorMessage,
  getJianyingTemplateRuntimeStatus,
} from './useJianyingCreateWorkspace.js';

const toSubmittedImageValue = (value = {}) => ({
  kind: 'image',
  image_id: value.image_id,
});

const useJianyingJobSubmission = ({
  navigate,
  orderId,
  workspace,
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [audioUploadStatus, setAudioUploadStatus] = useState(null);
  const [videoUploadStatus, setVideoUploadStatus] = useState(null);
  const {
    applyFieldErrors,
    applyImageFieldErrors,
    audioSlots,
    imageSlots,
    preferredDeviceId,
    selectedCopyPresetId,
    selectedTemplate,
    setAudioFieldErrors,
    setFieldErrors,
    setImageFieldErrors,
    setMessage,
    setSlotValues,
    setVideoFieldErrors,
    showMaterials,
    slotValues,
    templateAvailability,
    uploadedAudioKeys,
    uploadedVideoKeys,
    videoSlots,
  } = workspace;

  const submitJob = async () => {
    if (!orderId || !selectedTemplate) return;
    const runtimeStatus = getJianyingTemplateRuntimeStatus(
      selectedTemplate,
      templateAvailability,
    );
    if (runtimeStatus !== JIANYING_TEMPLATE_RUNTIME_STATUS.AVAILABLE) return;
    const validation = validateJianyingSlotValues(selectedTemplate, slotValues);
    const hasFieldErrors = Object.keys(validation.fieldErrors).length > 0;
    const hasAudioFieldErrors = Object.keys(validation.audioFieldErrors).length > 0;
    const hasImageFieldErrors = Object.keys(validation.imageFieldErrors).length > 0;
    const hasVideoFieldErrors = Object.keys(validation.videoFieldErrors).length > 0;
    if (
      hasFieldErrors
      || hasAudioFieldErrors
      || hasImageFieldErrors
      || hasVideoFieldErrors
      || validation.pageErrors.length
    ) {
      showMaterials();
      const validationMessage = [
        hasFieldErrors ? '请修改标记的文字内容' : '',
        hasImageFieldErrors ? '请选择完整的图片素材' : '',
        hasAudioFieldErrors ? '请选择有效的音频素材' : '',
        hasVideoFieldErrors ? '请选择有效的视频素材和时间范围' : '',
        ...validation.pageErrors,
      ].filter(Boolean).join('；');
      setAudioFieldErrors(validation.audioFieldErrors);
      setImageFieldErrors(validation.imageFieldErrors);
      setVideoFieldErrors(validation.videoFieldErrors);
      if (hasFieldErrors) applyFieldErrors(validation.fieldErrors, validationMessage);
      else if (hasImageFieldErrors) {
        setFieldErrors({});
        applyImageFieldErrors(validation.imageFieldErrors, validationMessage);
      } else {
        setFieldErrors({});
        setMessage(validationMessage);
      }
      return;
    }

    setSubmitting(true);
    setMessage('');
    setFieldErrors({});
    setAudioFieldErrors({});
    setImageFieldErrors({});
    setVideoFieldErrors({});
    try {
      const workingSlotValues = Object.fromEntries(
        Object.entries(slotValues).map(([slotId, value]) => [
          slotId,
          value && typeof value === 'object' ? { ...value } : value,
        ]),
      );
      const submittedSlotValues = { ...workingSlotValues };
      for (const slot of imageSlots) {
        if (!workingSlotValues[slot.id]) continue;
        submittedSlotValues[slot.id] = toSubmittedImageValue(
          workingSlotValues[slot.id],
        );
      }
      const pendingAudioSlots = audioSlots.filter(
        (slot) => submittedSlotValues[slot.key]?.file
          && !submittedSlotValues[slot.key]?.object_key,
      );
      for (const [index, slot] of pendingAudioSlots.entries()) {
        const value = submittedSlotValues[slot.key];
        setAudioUploadStatus({
          current: index + 1,
          total: pendingAudioSlots.length,
        });
        let uploadedValue;
        try {
          uploadedValue = await uploadJianyingJobAudio({
            clientId: orderId,
            templateId: selectedTemplate.id,
            slotId: slot.id,
            file: value.file,
            durationUs: value.duration_us,
          });
        } catch (error) {
          setAudioFieldErrors((current) => ({
            ...current,
            [slot.key]: getJianyingCreateErrorMessage(error, '音频上传失败'),
          }));
          throw error;
        }
        uploadedAudioKeys.current.add(uploadedValue.object_key);
        submittedSlotValues[slot.key] = uploadedValue;
        setSlotValues((current) => ({
          ...current,
          [slot.key]: { ...uploadedValue, file: value.file },
        }));
      }
      for (const slot of audioSlots) {
        const value = submittedSlotValues[slot.key];
        if (!value?.object_key) continue;
        submittedSlotValues[slot.key] = {
          kind: 'audio',
          object_key: value.object_key,
          file_name: value.file_name,
          duration_us: value.duration_us,
        };
      }
      const pendingVideoSlots = videoSlots.filter(
        (slot) => submittedSlotValues[slot.key]?.file
          && !submittedSlotValues[slot.key]?.object_key,
      );
      for (const [index, slot] of pendingVideoSlots.entries()) {
        const value = submittedSlotValues[slot.key];
        setVideoUploadStatus({
          current: index + 1,
          total: pendingVideoSlots.length,
        });
        let uploadedValue;
        try {
          uploadedValue = await uploadJianyingJobVideo({
            clientId: orderId,
            templateId: selectedTemplate.id,
            slotId: slot.id,
            file: value.file,
            fileDurationUs: value.file_duration_us,
            sourceStartUs: value.source_start_us,
            sourceDurationUs: value.source_duration_us,
          });
        } catch (error) {
          setVideoFieldErrors((current) => ({
            ...current,
            [slot.key]: getJianyingCreateErrorMessage(error, '视频上传失败'),
          }));
          throw error;
        }
        uploadedVideoKeys.current.add(uploadedValue.object_key);
        submittedSlotValues[slot.key] = uploadedValue;
        setSlotValues((current) => ({
          ...current,
          [slot.key]: { ...uploadedValue, file: value.file },
        }));
      }
      for (const slot of videoSlots) {
        const value = submittedSlotValues[slot.key];
        if (!value?.object_key) continue;
        submittedSlotValues[slot.key] = {
          kind: 'video',
          object_key: value.object_key,
          file_name: value.file_name,
          file_duration_us: value.file_duration_us,
          source_start_us: value.source_start_us,
          source_duration_us: value.source_duration_us,
        };
      }
      setAudioUploadStatus(null);
      setVideoUploadStatus(null);
      const job = await createJianyingJob({
        clientId: orderId,
        copyPresetId: selectedCopyPresetId,
        templateId: selectedTemplate.id,
        slotValues: submittedSlotValues,
        preferDeviceId: preferredDeviceId || null,
      });
      uploadedAudioKeys.current.clear();
      uploadedVideoKeys.current.clear();
      navigate(`/automation/jianying/list?job_id=${encodeURIComponent(job.id)}`);
    } catch (error) {
      const responseData = error.response?.data || {};
      const errorMessage = getJianyingCreateErrorMessage(error, '创建剪映任务失败');
      if (responseData.field_errors) {
        applyFieldErrors(responseData.field_errors, errorMessage);
      } else {
        setMessage(errorMessage);
      }
    } finally {
      setAudioUploadStatus(null);
      setVideoUploadStatus(null);
      setSubmitting(false);
    }
  };

  const submitButtonLabel = videoUploadStatus
      ? `上传视频 ${videoUploadStatus.current}/${videoUploadStatus.total}`
      : audioUploadStatus
        ? `上传音频 ${audioUploadStatus.current}/${audioUploadStatus.total}`
        : submitting
          ? '创建中…'
          : '创建任务';

  return {
    submitButtonLabel,
    submitJob,
    submitting,
  };
};

export default useJianyingJobSubmission;
